import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, DraftingCompass, Anchor, Headset, HardHat } from "lucide-react";
import { MaskedLine, EASE, FadeIn, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import {
    SERVICES,
    HERO_IMAGE,
    WELDING_IMAGE,
    FABRICATION_IMAGE,
    CONTACT,
} from "@/data/site";

const WHY = [
    {
        icon: "compass",
        title: "Engineering Solutions",
        text: "From mono rail systems to suspended platforms — lifting equipment engineered around your site, your loads and your workflow.",
        span: "md:col-span-2",
        image: FABRICATION_IMAGE,
    },
    {
        icon: "anchor",
        title: "Reliable Systems",
        text: "Built to perform day after day on demanding sites, with structure and mechanics you can depend on.",
        span: "",
        image: null,
    },
    {
        icon: "headset",
        title: "Professional Service",
        text: "Straightforward advice, clear communication and dependable support — from first enquiry to installation and after.",
        span: "",
        image: null,
    },
    {
        icon: "hardhat",
        title: "Safety-First Design",
        text: "Safety is engineered into every system we deliver — because lifting equipment is only as good as the way it protects the people who use it.",
        span: "md:col-span-2",
        image: WELDING_IMAGE,
    },
];

const ICONS = {
    compass: DraftingCompass,
    anchor: Anchor,
    headset: Headset,
    hardhat: HardHat,
};

export default function Home() {
    const heroRef = useRef(null);
    const [active, setActive] = useState(0);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section
                ref={heroRef}
                data-testid="hero-section"
                className="relative flex min-h-[100svh] flex-col overflow-hidden"
            >
                <motion.div style={{ y: bgY }} className="absolute -top-[10%] left-0 h-[120%] w-full">
                    <motion.img
                        src={HERO_IMAGE}
                        alt="Tower cranes over a construction site at dusk"
                        className="h-full w-full object-cover"
                        initial={{ scale: 1.12 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 2.2, ease: EASE }}
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-b from-night/80 via-night/55 to-night" />
                <div className="absolute inset-0 bg-gradient-to-r from-night/80 via-transparent to-night/40" />
                <div className="grain inset-0" />

                <motion.div
                    style={{ opacity: contentOpacity, y: contentY }}
                    className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pt-28 sm:px-6 lg:px-12"
                >
                    <MaskedLine delay={0.15}>
                        <p className="flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-accent sm:text-xs">
                            <span className="h-px w-12 bg-accent" aria-hidden="true" />
                            Lifting & Access Systems — Bangalore
                        </p>
                    </MaskedLine>

                    <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.9] tracking-tight text-[clamp(3.8rem,11vw,10rem)]">
                        <MaskedLine delay={0.28}>
                            <span className="text-white">Uma</span>
                        </MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-stroke">
                                Engineering
                                <span className="text-accent" style={{ WebkitTextStroke: "0" }}>
                                    .
                                </span>
                            </span>
                        </MaskedLine>
                    </h1>

                    <MaskedLine delay={0.55}>
                        <p className="mt-6 max-w-2xl text-lg font-light text-slate-300 sm:text-2xl">
                            Professional Engineering &{" "}
                            <span className="font-semibold text-white">Lifting Solutions</span>
                        </p>
                    </MaskedLine>
                    <MaskedLine delay={0.65}>
                        <p className="mt-3 font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate-400 sm:text-xs">
                            Engineering Solutions • Reliable Systems • Professional Service
                        </p>
                    </MaskedLine>

                    <MaskedLine delay={0.78}>
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                            <Link
                                to="/services"
                                data-testid="hero-cta-our-services"
                                className="group flex items-center justify-center gap-3 bg-accent px-9 py-4 font-display text-base font-bold uppercase tracking-widest text-night transition-colors duration-300 hover:bg-accent-hover"
                            >
                                Our Services
                                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact-us"
                                className="flex items-center justify-center gap-3 border border-slate-500/60 px-9 py-4 font-display text-base font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-accent hover:text-accent"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </MaskedLine>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="relative z-10 border-t border-white/10"
                >
                    <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-12">
                        <div className="flex items-center gap-3 font-mono text-[0.6rem] uppercase tracking-[0.3em] text-slate-400">
                            <span className="relative h-8 w-px overflow-hidden bg-white/20">
                                <motion.span
                                    className="absolute left-0 top-0 h-3 w-px bg-accent"
                                    animate={{ y: [-12, 32] }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                                />
                            </span>
                            Scroll
                        </div>
                        <p className="hidden font-mono text-[0.6rem] uppercase tracking-[0.3em] text-slate-400 md:block">
                            Hebbagodi — Bengaluru 560 099
                        </p>
                    </div>
                </motion.div>
            </section>

            <Marquee />

            {/* SERVICES INDEX */}
            <section className="py-20 lg:py-32">
                <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-12">
                    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <SectionHeading
                            eyebrow="What we build"
                            title={
                                <>
                                    Lifting systems for
                                    <br />
                                    every <span className="text-accent">site</span>
                                </>
                            }
                        />
                        <FadeIn delay={0.15}>
                            <Link
                                to="/services"
                                data-testid="home-view-all-services-btn"
                                className="group inline-flex items-center gap-2 border border-line px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-accent hover:text-accent"
                            >
                                View All Services
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </FadeIn>
                    </div>

                    <div className="relative mt-14">
                        <div
                            className="pointer-events-none absolute right-0 top-1/2 z-10 hidden w-[360px] -translate-y-1/2 xl:block"
                            aria-hidden="true"
                        >
                            <motion.img
                                key={active}
                                src={SERVICES[active].image}
                                alt=""
                                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.35, ease: EASE }}
                                className="aspect-[4/3] w-full border border-line object-cover shadow-2xl"
                            />
                        </div>
                        <ul>
                            {SERVICES.map((s, i) => (
                                <motion.li
                                    key={s.id}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                                >
                                    <Link
                                        to="/services"
                                        data-testid={`service-row-${s.id}`}
                                        onMouseEnter={() => setActive(i)}
                                        className="group flex items-center justify-between gap-4 border-t border-line py-7 pr-2 transition-all duration-300 last:border-b hover:pl-4 xl:pr-[420px]"
                                    >
                                        <div className="flex items-baseline gap-5 sm:gap-8">
                                            <span className="font-mono text-xs text-accent sm:text-sm">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <div>
                                                <h3 className="font-display text-3xl font-bold uppercase leading-none text-slate-300 transition-colors duration-300 group-hover:text-white sm:text-4xl lg:text-5xl">
                                                    {s.name}
                                                </h3>
                                                <p className="mt-2 max-w-md text-xs text-slate-500 sm:text-sm">
                                                    {s.blurb}
                                                </p>
                                            </div>
                                        </div>
                                        <ArrowUpRight className="h-6 w-6 shrink-0 -translate-x-2 text-accent opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                                    </Link>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* WHY UMA BENTO */}
            <section className="border-t border-line bg-deck py-20 lg:py-32">
                <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-12">
                    <SectionHeading
                        eyebrow="Why UMA Engineering"
                        title={
                            <>
                                Built on the way
                                <br />
                                we <span className="text-accent">work</span>
                            </>
                        }
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {WHY.map((card, i) => {
                            const Icon = ICONS[card.icon];
                            return (
                                <motion.div
                                    key={card.title}
                                    data-testid={`why-card-${card.icon}`}
                                    initial={{ opacity: 0, y: 32 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                                    className={`group relative overflow-hidden border border-line bg-card p-8 transition-colors duration-500 hover:border-accent/50 ${card.span}`}
                                >
                                    {card.image && (
                                        <>
                                            <img
                                                src={card.image}
                                                alt=""
                                                className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-700 group-hover:scale-105 group-hover:opacity-35"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-card/30" />
                                        </>
                                    )}
                                    <div className="relative">
                                        <span className="flex h-12 w-12 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white sm:text-3xl">
                                            {card.title}
                                        </h3>
                                        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                                            {card.text}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section className="relative overflow-hidden border-t border-line py-20 lg:py-28">
                <div className="grain inset-0" />
                <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-start gap-10 px-4 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-12">
                    <div>
                        <FadeIn>
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                                Get in touch
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.1}>
                            <h2 className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.92] text-white sm:text-6xl lg:text-7xl">
                                Have a lifting
                                <br />
                                <span className="text-stroke">challenge?</span>
                            </h2>
                        </FadeIn>
                    </div>
                    <FadeIn delay={0.2} className="flex flex-col items-start gap-5">
                        <Link
                            to="/contact"
                            data-testid="home-cta-enquire-btn"
                            className="group flex items-center gap-3 bg-accent px-9 py-4 font-display text-base font-bold uppercase tracking-widest text-night transition-colors duration-300 hover:bg-accent-hover"
                        >
                            Enquire Now
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                        <a
                            href={CONTACT.phones[0].href}
                            data-testid="home-cta-call-link"
                            className="font-mono text-sm text-slate-400 transition-colors hover:text-accent"
                        >
                            or call {CONTACT.phones[0].display}
                        </a>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
}
